import { useEffect, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { copy } from "../copy";
import BrandLogo from "../components/BrandLogo";
import Icon, { type IconName } from "../components/Icon";

type Props = {
  title: string;
  eyebrow: string;
  heading: readonly [string, string];
  sub: string;
  children: ReactNode;
  footer: ReactNode;
};

// The template's split auth layout: a brand panel from lg, the form on the
// right. The brand panel uses the smoke haze instead of a stock photo.
export default function AuthShell({ title, eyebrow, heading, sub, children, footer }: Props) {
  useEffect(() => {
    document.title = title;
  }, [title]);

  return (
    <div className="min-h-screen bg-ground font-sans text-chrome antialiased lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,560px)]">
      <aside className="relative hidden overflow-hidden border-r border-ground-edge lg:flex lg:flex-col">
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="absolute -left-40 top-1/4 h-[520px] w-[720px] rounded-full bg-money/10 blur-3xl" />
          <div className="absolute -bottom-40 right-0 h-[420px] w-[520px] rounded-full bg-gold/10 blur-3xl" />
          <div className="smoke absolute inset-0 animate-smoke motion-reduce:animate-none" />
        </div>
        <div className="relative z-10 flex h-full flex-col justify-between p-10 xl:p-14">
          <Link to="/" aria-label={copy.brand} className="bf-focusable flex w-fit items-center">
            <BrandLogo className="h-12" />
          </Link>
          <div className="max-w-md">
            <p className="text-[11px] uppercase tracking-[0.24em] text-gold">{copy.auth.brandEyebrow}</p>
            <h2 className="mt-5 font-headline text-4xl uppercase leading-[0.95] tracking-[-0.02em] text-chrome xl:text-5xl">
              {copy.auth.brandHeading}
            </h2>
            <p className="mt-5 text-sm leading-relaxed text-chrome-dim">{copy.auth.brandBody}</p>
            <ul className="mt-8 flex flex-col gap-4">
              {copy.auth.brandPoints.map((point) => (
                <li key={point.text} className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-ground-edge bg-ground-raised text-base text-gold">
                    <Icon name={point.icon as IconName} />
                  </span>
                  <span className="pt-1 text-sm leading-snug text-chrome-dim">{point.text}</span>
                </li>
              ))}
            </ul>
          </div>
          <p className="text-[11px] uppercase tracking-[0.16em] text-chrome-dim">{copy.auth.rights}</p>
        </div>
      </aside>

      <main className="flex min-h-screen flex-col justify-center px-5 py-12 md:px-10">
        <div className="bf-rise mx-auto w-full max-w-[420px]">
          <Link to="/" aria-label={copy.brand} className="bf-focusable mb-10 flex w-fit items-center lg:hidden">
            <BrandLogo className="h-10" />
          </Link>
          <p className="text-[11px] uppercase tracking-[0.24em] text-chrome-dim">{eyebrow}</p>
          <h1 className="mt-4 font-headline text-4xl uppercase leading-[0.95] tracking-[-0.02em] text-chrome md:text-5xl">
            {heading[0]} <span className="text-gold">{heading[1]}</span>
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-chrome-dim">{sub}</p>
          <div className="mt-9">{children}</div>
          <div className="mt-8 border-t border-ground-edge pt-6 text-center text-sm text-chrome-dim">{footer}</div>
        </div>
      </main>
    </div>
  );
}

export function AuthAlert({ message }: { message: string }) {
  return (
    <div role="alert" className="bf-fade-in flex items-start gap-2 rounded-md border border-alert/40 bg-alert/10 px-4 py-3">
      <Icon name="errorWarning" className="mt-0.5 text-base text-alert" />
      <p className="text-xs leading-relaxed text-chrome">{message}</p>
    </div>
  );
}
